import { env } from "@beztack/env/ui";
import { useEffect, useRef } from "react";

const PING_INTERVAL_MS = 7000;

const apiUrl = (path: string) => `${env.VITE_API_URL}${path}`;

type Active = {
  deliveryId: string;
  enabled: boolean;
};

export function useGpsTracker(active: Active) {
  const watchRef = useRef<number | null>(null);
  const lastSentRef = useRef<number>(0);

  useEffect(() => {
    if (!active.enabled || !navigator.geolocation) {
      return;
    }

    const sendPing = (position: GeolocationPosition) => {
      const now = Date.now();
      if (now - lastSentRef.current < PING_INTERVAL_MS) {
        return;
      }
      lastSentRef.current = now;

      const payload = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy ?? null,
        speed: position.coords.speed ?? null,
        heading: position.coords.heading ?? null,
        capturedAt: new Date(position.timestamp).toISOString(),
      };

      fetch(apiUrl(`/api/deliveries/${active.deliveryId}/location`), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() => {
        // Best-effort; service worker background sync would be a v2 enhancement.
      });
    };

    const id = navigator.geolocation.watchPosition(
      sendPing,
      () => {
        /* errors silently swallowed; UI shows enabled state separately */
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15_000 },
    );

    watchRef.current = id;

    return () => {
      if (watchRef.current != null) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
    };
  }, [active.enabled, active.deliveryId]);
}
